import { useCallback, useEffect, useState } from "react";
import {
  Inbox,
  Phone,
  Mail,
  MapPin,
  Home,
  Calendar,
  Paperclip,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Trash2,
  Copy,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../../components/ui/sonner";

const STATUS_LABEL = {
  ny: { label: "Ny", color: "bg-[#D95D2E] text-white" },
  kontaktad: { label: "Kontaktad", color: "bg-[#2B4A6F] text-white" },
  klar: { label: "Klar", color: "bg-[#1B2A1E] text-white" },
};

const PAGE_SIZE = 20;

const SERVICE_LABELS = {
  tradfallning: "Trädfällning",
  beskarning: "Beskärning / trädvård",
  stubbfrasning: "Stubbfräsning",
  bortforsling: "Bortforsling",
  "mark-och-anlaggning": "Mark & anläggning",
  "gronyteskotsel-och-hackklippning": "Grönyteskötsel / häckklippning",
  snorojning: "Snöröjning",
  motorsagskorkort: "Motorsågskörkort A+B",
  ovrigt: "Annat",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatSwedishDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const months = [
      "januari",
      "februari",
      "mars",
      "april",
      "maj",
      "juni",
      "juli",
      "augusti",
      "september",
      "oktober",
      "november",
      "december",
    ];
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} kl. ${hh}.${mm}`;
  } catch {
    return iso;
  }
}

function serviceLabel(raw) {
  if (!raw) return "";
  return raw
    .split(",")
    .map((s) => SERVICE_LABELS[s.trim()] || s.trim())
    .filter(Boolean)
    .join(", ");
}

function buildCopyText(q, origin) {
  const lines = [];
  const svc = serviceLabel(q.service);
  lines.push(`Ny offertförfrågan${svc ? ` – ${svc}` : ""}`);
  lines.push("");
  if (q.name) lines.push(`Namn: ${q.name}`);
  if (q.phone) lines.push(`Telefon: ${q.phone}`);
  if (q.email) lines.push(`E-post: ${q.email}`);
  if (q.city) lines.push(`Ort: ${q.city}`);
  if (q.street_address) lines.push(`Gatuadress: ${q.street_address}`);
  if (svc) lines.push(`Tjänst: ${svc}`);
  if (q.description) {
    lines.push("");
    lines.push("Beskrivning:");
    lines.push(q.description);
  }
  if (q.image_url) {
    const url = q.image_url.startsWith("http")
      ? q.image_url
      : `${origin || ""}${q.image_url}`;
    lines.push("");
    lines.push(`Bifogad bild: ${url}`);
  }
  if (q.created_at) {
    lines.push("");
    lines.push(`Mottaget: ${formatSwedishDateTime(q.created_at)}`);
  }
  return lines.join("\n");
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, ny: 0, kontaktad: 0, klar: 0 });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const load = useCallback(async ({ page: nextPage, filter: nextFilter }) => {
    setLoading(true);
    try {
      const params = { page: nextPage, page_size: PAGE_SIZE };
      if (nextFilter !== "all") params.status = nextFilter;
      const r = await api.get("/admin/quotes", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
      setCounts(r.data.counts || { all: 0, ny: 0, kontaktad: 0, klar: 0 });
    } catch {
      toast.error("Kunde inte hämta förfrågningar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ page, filter });
  }, [page, filter, load]);

  const changeFilter = (v) => {
    if (v === filter) return;
    setActiveId(null);
    setFilter(v);
    setPage(1); // reset paginering vid filter-byte
  };

  const changePage = (n) => {
    if (n < 1) return;
    setActiveId(null);
    setPage(n);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}/status`, { status });
      // Lokalt: uppdatera det aktiva itemet så detaljvyn ändras direkt.
      setItems((arr) => arr.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status uppdaterad");
      // Räkningarna kan ha ändrats — omladda tyst.
      load({ page, filter });
    } catch {
      toast.error("Kunde inte uppdatera status");
    }
  };

  const deleteQuote = async (id) => {
    const q = items.find((x) => x.id === id);
    const name = q?.name || "förfrågan";
    if (
      !window.confirm(
        `Ta bort förfrågan från ${name}? Det går inte att ångra.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/admin/quotes/${id}`);
      if (activeId === id) setActiveId(null);
      toast.success("Förfrågan borttagen");
      // Omladda så paginering + räkningar stämmer; hoppa tillbaka en sida
      // om vi råkade tömma den aktuella sidan.
      const nextPage =
        items.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        load({ page: nextPage, filter });
      }
    } catch {
      toast.error("Kunde inte ta bort förfrågan");
    }
  };

  const active = activeId ? items.find((q) => q.id === activeId) : null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="admin-quotes-page">
      {/* Mobile: show detail as full-screen replacement so user doesn't
          scroll past the whole list to reach the selected item. */}
      {active && (
        <div className="lg:hidden">
          <QuoteDetail
            q={active}
            variant="mobile"
            onBack={() => setActiveId(null)}
            onStatus={(s) => updateStatus(active.id, s)}
            onDelete={() => deleteQuote(active.id)}
          />
        </div>
      )}

      {!(active && typeof window !== "undefined") ? null : (
        <style>{`
          /* On mobile only, hide the list when a quote is opened. */
          @media (max-width: 1023px) {
            [data-testid="admin-quotes-listwrap"] { display: none; }
          }
        `}</style>
      )}

      <div data-testid="admin-quotes-listwrap">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-[#1B2A1E]">
              Förfrågningar
            </h1>
            <p className="text-[#5A6258] mt-1 text-sm">
              {counts.all} inkomna · {counts.ny} nya
            </p>
          </div>
          <button
            onClick={() => load({ page, filter })}
            data-testid="admin-quotes-refresh"
            className="inline-flex items-center gap-2 bg-white border border-[#E2E0D8] hover:border-[#D95D2E] px-3 py-2 rounded-lg text-sm font-semibold"
          >
            <RefreshCw
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
            />
            Uppdatera
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { v: "all", label: "Alla", n: counts.all },
            { v: "ny", label: "Ny", n: counts.ny },
            { v: "kontaktad", label: "Kontaktad", n: counts.kontaktad },
            { v: "klar", label: "Klar", n: counts.klar },
          ].map((b) => (
            <button
              key={b.v}
              onClick={() => changeFilter(b.v)}
              data-testid={`admin-quotes-filter-${b.v}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${
                filter === b.v
                  ? "bg-[#1B2A1E] text-white"
                  : "bg-white border border-[#E2E0D8] text-[#3a4338] hover:border-[#D95D2E]"
              }`}
            >
              {b.label} · {b.n}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-[#5A6258] p-4">Laddar…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-quotes-empty"
            className="bg-white border border-[#E2E0D8] rounded-xl p-10 text-center"
          >
            <Inbox className="w-10 h-10 mx-auto mb-3 text-[#D95D2E]" />
            <h2 className="font-heading font-bold text-lg text-[#1B2A1E]">
              Inga förfrågningar
            </h2>
            <p className="text-[#5A6258] text-sm mt-1">
              När någon skickar en offertförfrågan dyker den upp här.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            <div className="lg:col-span-7">
              <ul className="space-y-3">
                {items.map((q) => {
                  const st = STATUS_LABEL[q.status] || STATUS_LABEL.ny;
                  return (
                    <li key={q.id}>
                      <button
                        onClick={() => setActiveId(q.id)}
                        data-testid={`admin-quote-item-${q.id}`}
                        className={`w-full text-left bg-white border rounded-xl p-4 hover:border-[#D95D2E] transition-colors ${
                          activeId === q.id
                            ? "border-[#D95D2E]"
                            : "border-[#E2E0D8]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] ${st.color}`}
                              >
                                {st.label}
                              </span>
                              <span className="text-xs text-[#5A6258]">
                                {formatDate(q.created_at)}
                              </span>
                            </div>
                            <h3 className="font-heading font-bold text-[#1B2A1E] truncate">
                              {q.name} · {serviceLabel(q.service)}
                            </h3>
                            <p className="text-sm text-[#3a4338] mt-1 line-clamp-2">
                              {q.description}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#5A6258] shrink-0 mt-1" />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={PAGE_SIZE}
                onChange={changePage}
              />
            </div>

            {/* Desktop right-column detail view */}
            <div className="hidden lg:block lg:col-span-5">
              {active ? (
                <QuoteDetail
                  q={active}
                  variant="desktop"
                  onStatus={(s) => updateStatus(active.id, s)}
                  onDelete={() => deleteQuote(active.id)}
                />
              ) : (
                <div className="bg-white border border-dashed border-[#E2E0D8] rounded-xl p-8 text-center text-[#5A6258]">
                  Välj en förfrågan i listan för att se detaljer.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, total, pageSize, onChange }) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // Bygger en kompakt sidnummer-lista: alltid 1, page-1, page, page+1, totalPages
  // med "…" mellan luckor.
  const pages = [];
  const push = (n) => {
    if (n < 1 || n > totalPages) return;
    if (pages[pages.length - 1] === n) return;
    pages.push(n);
  };
  push(1);
  if (page - 1 > 2) pages.push("…");
  for (let n = page - 1; n <= page + 1; n++) push(n);
  if (page + 1 < totalPages - 1) pages.push("…");
  push(totalPages);

  return (
    <nav
      data-testid="admin-quotes-pagination"
      aria-label="Sidnavigering"
      className="mt-6 flex flex-wrap items-center justify-between gap-3"
    >
      <div className="text-xs text-[#5A6258]">
        Visar <span className="font-semibold text-[#1B2A1E]">{from}–{to}</span>
        {" "}av <span className="font-semibold text-[#1B2A1E]">{total}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          data-testid="admin-quotes-page-prev"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.14em] bg-white border border-[#E2E0D8] text-[#3a4338] hover:border-[#D95D2E] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Föregående
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span
              key={`e${i}`}
              className="px-2 text-[#5A6258] text-xs select-none"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              data-testid={`admin-quotes-page-${p}`}
              aria-current={p === page ? "page" : undefined}
              className={`min-w-[36px] px-2 py-1.5 rounded-lg text-xs font-bold ${
                p === page
                  ? "bg-[#1B2A1E] text-white"
                  : "bg-white border border-[#E2E0D8] text-[#3a4338] hover:border-[#D95D2E]"
              }`}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          data-testid="admin-quotes-page-next"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.14em] bg-white border border-[#E2E0D8] text-[#3a4338] hover:border-[#D95D2E] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Nästa <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </nav>
  );
}

function QuoteDetail({ q, variant = "desktop", onBack, onStatus, onDelete }) {
  const desktop = variant === "desktop";
  const svc = serviceLabel(q.service);
  const origin =
    (typeof window !== "undefined" && window.location.origin) || "";
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const ok = await copyToClipboard(buildCopyText(q, origin));
    if (ok) {
      toast.success("Förfrågan kopierad");
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } else {
      toast.error("Kunde inte kopiera");
    }
  };
  return (
    <div
      data-testid={`admin-quote-detail-${variant}`}
      data-legacy-testid="admin-quote-detail"
      className={`bg-white border border-[#E2E0D8] rounded-xl p-5 ${
        desktop ? "sticky top-24" : ""
      }`}
    >
      {!desktop && (
        <button
          type="button"
          onClick={onBack}
          data-testid="admin-quote-back"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#3a4338] hover:text-[#D95D2E] mb-4"
        >
          <ChevronLeft className="w-4 h-4" /> Tillbaka till listan
        </button>
      )}

      <div className="flex items-center justify-between mb-4 gap-3">
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] ${
            STATUS_LABEL[q.status]?.color
          }`}
        >
          {STATUS_LABEL[q.status]?.label}
        </span>
        <select
          value={q.status}
          onChange={(e) => onStatus(e.target.value)}
          data-testid="admin-quote-status-select"
          className="text-sm bg-white border border-[#E2E0D8] rounded-md px-2 py-1.5 font-semibold text-[#1B2A1E]"
        >
          <option value="ny">Ny</option>
          <option value="kontaktad">Kontaktad</option>
          <option value="klar">Klar</option>
        </select>
      </div>

      <h3 className="font-heading text-xl font-bold text-[#1B2A1E]">
        {q.name}
      </h3>
      <div className="text-xs text-[#5A6258] mb-4">
        <Calendar className="inline w-3.5 h-3.5 mr-1" />
        {formatDate(q.created_at)}
      </div>

      <dl className="space-y-2 text-sm">
        <a
          href={`tel:${q.phone}`}
          data-testid="admin-quote-phone"
          className="flex items-center gap-2 text-[#1B2A1E] hover:text-[#D95D2E] font-semibold"
        >
          <Phone className="w-4 h-4 text-[#D95D2E]" /> {q.phone}
        </a>
        {q.email && (
          <a
            href={`mailto:${q.email}`}
            data-testid="admin-quote-email"
            className="flex items-center gap-2 text-[#1B2A1E] hover:text-[#D95D2E] font-semibold break-all"
          >
            <Mail className="w-4 h-4 text-[#D95D2E]" /> {q.email}
          </a>
        )}
        {q.city && (
          <div
            data-testid="admin-quote-city"
            className="flex items-center gap-2 text-[#3a4338]"
          >
            <MapPin className="w-4 h-4 text-[#D95D2E]" />
            <span>
              <span className="text-xs uppercase tracking-[0.14em] font-bold text-[#5A6258] mr-1.5">
                Ort:
              </span>
              {q.city}
            </span>
          </div>
        )}
        {q.street_address && (
          <div
            data-testid="admin-quote-street"
            className="flex items-center gap-2 text-[#3a4338]"
          >
            <Home className="w-4 h-4 text-[#D95D2E]" />
            <span>
              <span className="text-xs uppercase tracking-[0.14em] font-bold text-[#5A6258] mr-1.5">
                Gatuadress:
              </span>
              {q.street_address}
            </span>
          </div>
        )}
        <div className="pt-3 border-t border-[#E2E0D8]">
          <div className="text-xs uppercase tracking-[0.14em] font-bold text-[#5A6258] mb-1">
            Tjänst
          </div>
          <div className="font-semibold text-[#1B2A1E]">{svc}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.14em] font-bold text-[#5A6258] mb-1">
            Beskrivning
          </div>
          <p className="text-[#18221A] whitespace-pre-line">{q.description}</p>
        </div>
        {q.image_url && (
          <a
            href={q.image_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-quote-attachment"
            className="mt-3 inline-flex items-center gap-2 bg-[#EFEBDF] hover:bg-[#1B2A1E] hover:text-white text-[#1B2A1E] text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
          >
            <Paperclip className="w-3.5 h-3.5" /> Visa bifogad bild
          </a>
        )}
      </dl>

      <div className="mt-5 pt-4 border-t border-[#E2E0D8] flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={copy}
          data-testid="admin-quote-copy"
          className="inline-flex items-center gap-2 text-sm font-semibold bg-[#1B2A1E] hover:bg-[#283224] text-white px-3.5 py-2 rounded-lg transition-colors"
        >
          <Copy className="w-4 h-4" />
          {copied ? "Kopierat!" : "Kopiera förfrågan"}
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid="admin-quote-delete"
          className="inline-flex items-center gap-2 text-sm font-semibold text-red-700 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 px-3 py-2 rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" /> Ta bort förfrågan
        </button>
      </div>
    </div>
  );
}
